import { useEffect, useState } from "react";
import { useOptimisticUpdate } from "../../../hooks/useOptimisticUpdate.ts";
import { usePlanContext } from "../../../../auth/planProvider/PlanProvider.tsx";
import { isNullOrWhitespace } from "../../../stringOperations.ts";
import { deleteHttp, post } from "../../../api/apiShared.ts";
import { TagRelationCreateModel, TagRelationType } from "../TagsMultiSelect.types.ts";
import { useDotLegalQuery, useDotLegalMutation } from "@common/hooks/dotLegalReactQuery";

export function useTagsMultiSelectAutoUpdater(entityId: string, entityType: TagRelationType, disableQuery: boolean) {
    const [selectedTags, setSelectedTags] = useState<Array<string>>([]);

    const { putOnQueueAndSetQueryData } = useOptimisticUpdate();
    const { platformFeatures } = usePlanContext();
    const enableTagRelationsQuery = !isNullOrWhitespace(entityId) && platformFeatures.tags && !disableQuery;

    const tagRelationUrl = `/tagRelation/${entityId}`;

    const { isLoading, data } = useDotLegalQuery<Array<string>>(tagRelationUrl, {
        enabled: enableTagRelationsQuery,
    });

    useEffect(() => {
        if (data) {
            setSelectedTags(data);
        }
    }, [data]);

    const addTagRelationMutation = useDotLegalMutation(addTagRelationApi);
    const onAddTagRelation = async (tagId: string) => {
        const newRelation = createNewRelation(tagId);
        let tempTags = [...selectedTags];
        tempTags.push(tagId);
        putOnQueueAndSetQueryData(tempTags, tagRelationUrl, addTagRelationMutation.mutateAsync, newRelation);
    };

    function createNewRelation(tagId: string): TagRelationCreateModel {
        return { tagId: tagId, entityId: entityId!, entityType: entityType };
    }

    const removeTagRelationMutation = useDotLegalMutation(removeTagRelationApi);
    const onRemoveTagRelation = async (tagId: string) => {
        let tempRelations = [...selectedTags];
        tempRelations = tempRelations.filter((x) => x !== tagId);
        putOnQueueAndSetQueryData(tempRelations, tagRelationUrl, removeTagRelationMutation.mutateAsync, { entityId: entityId!, tagId: tagId });
    };

    const removeAllRelationsFromEntityMutation = useDotLegalMutation(removeRelationsFromEntityApi);
    const onRemoveAllRelationsFromEntity = async () => {
        removeAllRelationsFromEntityMutation.mutateAsync(entityId, {
            onSuccess: () => {
                setSelectedTags([]);
            },
        });
    };

    return {
        selectedTags,
        onAddTagRelation,
        onRemoveTagRelation,
        isLoading,
        onRemoveAllRelationsFromEntity,
    };

    async function addTagRelationApi(tagRelation: TagRelationCreateModel) {
        return await post("/tagRelation", tagRelation);
    }

    async function removeTagRelationApi(data: { entityId: string; tagId: string }) {
        return await deleteHttp(`/tagRelation/${data.entityId}?tagId=${data.tagId}`);
    }

    async function removeRelationsFromEntityApi(entityId: string) {
        return await deleteHttp(`/tagRelation/${entityId}/remove-relations`);
    }
}
